import { CSRF_HEADER } from "@ide/shared";
import type { UploadedMedia } from "./types";

export const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";
export const AI_BASE = (import.meta.env.VITE_AI_URL as string | undefined) ?? "/ai";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();
/** Avisa a aplicação quando a sessão expira (401) para voltar ao login. */
export function onUnauthorized(fn: Listener): () => void {
  unauthorizedListeners.add(fn);
  return () => {
    unauthorizedListeners.delete(fn);
  };
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  base?: string;
}

/**
 * Cliente da API: cookie de sessão HttpOnly (credentials: include) + cabeçalho
 * anti-CSRF em toda escrita. Erros viram ApiError com código estável.
 */
export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers["content-type"] = "application/json";
  if (method !== "GET") headers[CSRF_HEADER] = "1";
  const res = await fetch(`${opts.base ?? API_BASE}${path}`, {
    method,
    headers,
    credentials: "include",
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  if (!res.ok) {
    let payload: { error?: { code?: string; message?: string; details?: unknown } } = {};
    try {
      payload = await res.json();
    } catch {
      // resposta sem JSON
    }
    if (res.status === 401) unauthorizedListeners.forEach((l) => l());
    // Convidado (app desktop) tentou algo que exige conta: a interface oferece o login.
    if (payload.error?.code === "account_required" && method !== "GET") {
      window.dispatchEvent(new CustomEvent("kompil:account-required", { detail: payload.error.message }));
    }
    throw new ApiError(
      res.status,
      payload.error?.code ?? "http_error",
      payload.error?.message ?? `Erro ${res.status}`,
      payload.error?.details,
    );
  }
  if (res.status === 204) return undefined as T;
  const type = res.headers.get("content-type") ?? "";
  return (type.includes("application/json") ? res.json() : res.blob()) as Promise<T>;
}

export const get = <T>(path: string, signal?: AbortSignal) => api<T>(path, signal ? { signal } : {});
export const post = <T>(path: string, body?: unknown) => api<T>(path, { method: "POST", body: body ?? {} });
export const patch = <T>(path: string, body: unknown) => api<T>(path, { method: "PATCH", body });
export const put = <T>(path: string, body: unknown) => api<T>(path, { method: "PUT", body });
export const del = <T>(path: string) => api<T>(path, { method: "DELETE" });

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Algo deu errado.";
}

/** Baixa um arquivo de uma rota autenticada (ex.: exportar ZIP, exportar dados). */
export async function download(path: string, filename: string) {
  const res = await fetch(`${API_BASE}${path}`, { credentials: "include" });
  if (!res.ok) throw new ApiError(res.status, "download_failed", "Não foi possível baixar o arquivo.");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Envia uma imagem (avatar, portfólio ou chat). Antes, o navegador reduz a imagem para o tamanho
 * máximo do uso e a recodifica — o que também descarta metadados como a localização (GPS) da
 * foto. GIFs vão como estão (animação). O servidor confere tudo de novo.
 */
export async function uploadImage(
  file: Blob,
  purpose: "avatar" | "portfolio" | "chat" | "group",
  opts: { conversationId?: string } = {},
): Promise<UploadedMedia> {
  const { MEDIA_LIMITS } = await import("@ide/shared");
  const body = file.type === "image/gif" ? file : await shrinkImage(file, MEDIA_LIMITS.maxSide[purpose]);
  if (body.size > MEDIA_LIMITS.maxBytes[purpose]) {
    throw new ApiError(
      413,
      "too_large",
      `Imagem grande demais (máximo ${MEDIA_LIMITS.maxBytes[purpose] / 1024 / 1024} MB).`,
    );
  }
  const query = new URLSearchParams({
    purpose,
    ...(opts.conversationId ? { conversationId: opts.conversationId } : {}),
  });
  const res = await fetch(`${API_BASE}/media?${query}`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": body.type || "application/octet-stream", [CSRF_HEADER]: "1" },
    body,
  });
  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as { error?: { code?: string; message?: string } };
    throw new ApiError(
      res.status,
      payload.error?.code ?? "upload_failed",
      payload.error?.message ?? "Falha no envio.",
    );
  }
  return res.json();
}

/**
 * Anexo de arquivo numa conversa (documento, código, PDF, ZIP…): vai como está, com o nome
 * original. O servidor confere o conteúdo e recusa executáveis.
 */
export async function uploadFile(file: File, conversationId: string): Promise<UploadedMedia> {
  const { CHAT_FILE_LIMITS, isBlockedFileName } = await import("@ide/shared");
  if (isBlockedFileName(file.name))
    throw new ApiError(415, "invalid_file", "Executáveis e scripts do sistema não podem ser enviados.");
  if (file.size > CHAT_FILE_LIMITS.maxBytes) {
    throw new ApiError(
      413,
      "too_large",
      `Arquivo grande demais (máximo ${CHAT_FILE_LIMITS.maxBytes / 1024 / 1024} MB).`,
    );
  }
  const query = new URLSearchParams({ purpose: "chat", conversationId, file: "1", name: file.name });
  const res = await fetch(`${API_BASE}/media?${query}`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/octet-stream", [CSRF_HEADER]: "1" },
    body: file,
  });
  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as { error?: { code?: string; message?: string } };
    throw new ApiError(
      res.status,
      payload.error?.code ?? "upload_failed",
      payload.error?.message ?? "Falha no envio.",
    );
  }
  return res.json();
}

/** Redimensiona (lado maior ≤ maxSide) e recodifica em WebP (ou JPEG, se WebP não existir). */
export async function shrinkImage(file: Blob, maxSide: number): Promise<Blob> {
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) throw new ApiError(415, "invalid_image", "Não foi possível ler esta imagem.");
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new ApiError(415, "invalid_image", "Seu navegador não conseguiu processar a imagem.");
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const encode = (type: string, quality: number) =>
    new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));
  const webp = await encode("image/webp", 0.86);
  if (webp && webp.type === "image/webp") return webp;
  const jpeg = await encode("image/jpeg", 0.88);
  if (!jpeg) throw new ApiError(415, "invalid_image", "Não foi possível converter a imagem.");
  return jpeg;
}
